import { Link } from "react-router";

import { InitialsTile } from "../lib/InitialsTile.tsx";
import { Tag } from "../lib/Tag.tsx";
import { ACTIVITY_COPY, COLUMNS } from "./activity.constants.ts";
import type { AuditRow } from "./activity.model.ts";

/**
 * The trail, as a real table.
 *
 * Semantic markup because the data is genuinely tabular — four fields per row,
 * the same four every time — and a screen reader announcing "User, Michael
 * Hue-Williams" is the whole difference between a list and a log.
 *
 * The USER column's second line is the actor's ONDO id, where the reference
 * design puts a role. There is no role to print: `ActivityEntry.actor` carries
 * an id and a name and nothing else, deliberately, and no endpoint resolves a
 * principal. An id is what the registry recorded.
 */
export function ActivityTable({ rows, label }: { rows: AuditRow[]; label: string }) {
  return (
    <div className="activity__scroll">
      <table className="activity__table" aria-label={label}>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th key={column} scope="col" className="activity__head">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="activity__cell">
                {row.who ? (
                  <span className="activity__who">
                    <InitialsTile
                      className="activity__initials"
                      initials={row.who.initials}
                    />
                    <span>
                      <span className="activity__name">{row.who.name}</span>
                      <span className="activity__ondo-id">{row.who.ondoId}</span>
                    </span>
                  </span>
                ) : (
                  ACTIVITY_COPY.noActor
                )}
              </td>

              <td className="activity__cell">
                <span className="activity__glyph" aria-hidden="true">
                  {row.glyph}
                </span>
                {row.href ? (
                  <Link to={row.href} className="activity__action">
                    {row.action}
                  </Link>
                ) : (
                  <span className="activity__action">{row.action}</span>
                )}
                {row.subject ? (
                  <span className="activity__subject">{row.subject}</span>
                ) : null}
              </td>

              <td className="activity__cell">
                <Tag tone={row.tag.tone}>{row.tag.label}</Tag>
              </td>

              <td className="activity__cell activity__when">
                <time dateTime={row.isoTime}>{row.when}</time>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
